import { User } from 'lucide-react';
import React, { useMemo } from 'react';

import {
  AssigneeDropdown,
  type AssigneeOption,
  type AssigneeOptionGroup,
} from '@/components/AssigneeDropdown';
import type { AssigneeGroup } from '@/hooks/useAssigneeOptions';

interface BulkAssigneeDropdownProps {
  selectedCount: number;
  canBulkAssign: boolean;
  assigneeGroups: AssigneeGroup[];
  onAssigneeSelect: (assigneeEmail: string, teamId?: string) => Promise<void>;
  disabled?: boolean;
}

export function BulkAssigneeDropdown({
  selectedCount,
  canBulkAssign,
  assigneeGroups = [],
  onAssigneeSelect,
  disabled = false,
}: BulkAssigneeDropdownProps) {
  const hasUsers = assigneeGroups.length > 0;
  const isDisabled = disabled || !canBulkAssign || selectedCount === 0 || !hasUsers;

  const options = useMemo<AssigneeOptionGroup[]>(
    () =>
      assigneeGroups.map(group => ({
        id: group.teamId,
        label: group.teamName,
        options: group.members.map<AssigneeOption>(member => ({
          id: member.userId,
          name: member.name,
          email: member.email,
        })),
      })),
    [assigneeGroups],
  );

  const tooltip = (() => {
    if (selectedCount === 0) return 'No cases selected';
    if (!canBulkAssign)
      return 'Selected cases cannot be assigned (must be in human call/audit state';
    if (!hasUsers) return 'No available users to assign';
    return `Assign ${selectedCount} selected cases`;
  })();

  return (
    <AssigneeDropdown
      mode="single"
      variant="ghost"
      selected=""
      options={options}
      disabled={isDisabled}
      disabledTooltip={isDisabled ? tooltip : undefined}
      triggerLabel="Change Assignee"
      triggerIcon={User}
      onChange={(email, teamId) => {
        if (email) void onAssigneeSelect(email, teamId);
      }}
      searchPlaceholder="Search assignees..."
    />
  );
}
